import axios, { AxiosInstance } from 'axios'
import toast from 'react-hot-toast'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api', // Usar proxy en desarrollo o VITE_API_BASE_URL si está configurado
      timeout: 60000, // Aumentado a 60 segundos para operaciones más pesadas
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Interceptor de solicitud
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // Marcar requests a endpoints silenciosos para no loguear errores
        const silent404Endpoints = ['/news/public', '/news/all']
        if (silent404Endpoints.some(endpoint => config.url?.includes(endpoint))) {
          (config as any).__silent404 = true
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Interceptor de respuesta
    this.client.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        const currentPath = window.location.pathname
        const isLoginPage = currentPath === '/login'
        const requestUrl = error.config?.url || ''
        const isSilent404 = (error.config as any)?.__silent404 || false
        
        // Endpoints que pueden retornar 404 y no deben mostrar errores ni toasts
        const silent404Endpoints = ['/news/public', '/news/all']
        const isSilent404Endpoint = error.response?.status === 404 && 
          (isSilent404 || silent404Endpoints.some(endpoint => requestUrl.includes(endpoint)))
        
        // Si es un 404 silencioso, retornar el error sin procesar ni loguear (el componente manejará el error)
        if (isSilent404Endpoint) {
          // Suprimir el log de consola de Axios para este error
          // Crear un error personalizado que no se loguee
          const silentError = new Error('Silent 404')
          silentError.name = 'Silent404Error'
          ;(silentError as any).response = error.response
          ;(silentError as any).config = error.config
          // Prevenir que Axios loguee este error en consola
          if (error.config) {
            error.config.__isSilent = true
          }
          return Promise.reject(silentError)
        }
        
        // Solo redirigir a login si es una ruta protegida y el usuario estaba autenticado
        if (error.response?.status === 401) {
          const token = localStorage.getItem('token')
          
          // Lista de rutas públicas que no requieren autenticación
          const publicRoutes = ['/login', '/register', '/', '/stores', '/stores/']
          const isPublicRoute = publicRoutes.some(route => 
            currentPath === route || currentPath.startsWith(route + '/')
          )
          
          // Si estamos en la página de login, mostrar el mensaje de error del backend
          if (isLoginPage && error.response?.data?.message) {
            // El mensaje se mostrará en el componente Login, no aquí
            // No mostrar toast aquí para evitar duplicados
          }
          // Solo redirigir si había un token (usuario estaba autenticado) y no es una ruta pública
          else if (token && !isPublicRoute) {
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            toast.error('Sesión expirada. Por favor inicia sesión nuevamente.')
          }
          // Si no hay token y no es ruta pública, no mostrar error (es normal para rutas públicas)
        } else if (error.response?.status >= 500) {
          toast.error('Error del servidor. Por favor intenta más tarde.')
        } else if (error.response?.data?.message) {
          // Mostrar error solo si no estamos en login o register (para evitar duplicados)
          // o si es un error diferente a 401
          const isRegisterPage = currentPath === '/register'
          if ((!isLoginPage && !isRegisterPage) || error.response?.status !== 401) {
            toast.error(error.response.data.message)
          }
        } else if (!error.response) {
          // Usar el gestor centralizado para evitar toasts duplicados
          import('./toastManager').then(({ showConnectionErrorToast }) => {
            showConnectionErrorToast()
          })
        }

        return Promise.reject(error)
      }
    )
  }

  // Auth methods
  async login(credentials: { email: string; password: string }) {
    const response = await this.client.post('/auth/login', credentials)
    return response.data
  }

  async register(userData: any) {
    const response = await this.client.post('/auth/register', userData)
    return response.data
  }

  async requestStoreOwner() {
    const response = await this.client.post('/auth/request-store-owner', {})
    return response.data
  }

  async getStoreOwnerRequest() {
    const response = await this.client.get('/auth/my-store-owner-request')
    return response.data
  }

  async createStore(storeData: any) {
    const response = await this.client.post('/auth/create-store', storeData)
    return response.data
  }

  // Store Owner APIs - Multiple Stores Support
  async getMyStores() {
    const response = await this.client.get('/stores/my-stores')
    return response.data.data || { approved: [], pending: [], rejected: [], suspended: [], total: 0, active: 0 }
  }

  async getMyStore(storeId: string) {
    const response = await this.client.get(`/stores/my-stores/${storeId}`)
    return response.data
  }

  /**
   * Obtiene el estado del sistema desde el backend REST API
   * Incluye información del servidor, servicios (DB, Storage, Email, Queue), métricas y estadísticas
   * @returns Promise con la respuesta del servidor que incluye el estado completo del sistema
   */
  async getSystemStatus() {
    const response = await this.client.get('/admin/system-status')
    return response.data
  }

  /**
   * Verifica el estado de salud del servidor WebSocket
   * Realiza un health check al endpoint /health del servidor WebSocket para obtener:
   * - Estado de conexión (online/offline)
   * - Número de conexiones activas en el servidor
   * - Tiempo de actividad (uptime) del servidor
   * - Timestamp de la última verificación
   * 
   * Esta función se usa en el panel de administración para monitorear el servicio WebSocket
   * en tiempo real. Si el servidor no está disponible, retorna un estado offline con información
   * del error.
   * 
   * @returns Promise con un objeto que contiene:
   *   - success: boolean indicando si la petición fue exitosa
   *   - data: objeto con el estado del servidor WebSocket:
   *     - status: 'online' | 'offline'
   *     - connections: número de conexiones activas
   *     - uptime: tiempo de actividad en segundos
   *     - timestamp: ISO string de la última verificación
   *     - error: mensaje de error si el servidor está offline
   * 
   * @example
   * const health = await api.getWebSocketHealth()
   * if (health.success && health.data.status === 'online') {
   *   console.log(`WebSocket online con ${health.data.connections} conexiones`)
   * }
   */
  async getWebSocketHealth() {
    // Obtener la URL del servidor WebSocket desde las variables de entorno
    // Por defecto usa localhost:3002 para desarrollo
    const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:3002'
    
    // Solo loguear si está explícitamente habilitado
    const DEBUG_API = import.meta.env.VITE_DEBUG_API === 'true'
    if (DEBUG_API) {
      console.log('[API] Verificando salud del WebSocket en:', wsUrl)
    }
    
    try {
      // Realizar petición HTTP GET al endpoint /health del servidor WebSocket
      // Timeout de 5 segundos para evitar esperas prolongadas
      const response = await axios.get(`${wsUrl}/health`, {
        timeout: 5000
      })
      
      if (DEBUG_API) {
        console.log('[API] Respuesta del health check:', response.data)
      }
      
      // Si la petición fue exitosa, retornar el estado como online
      // El servidor retorna 'ok' pero necesitamos 'online' para consistencia
      const { status: serverStatus, ...restData } = response.data
      return {
        success: true,
        data: {
          status: serverStatus === 'ok' ? 'online' : 'offline', // Convertir 'ok' a 'online'
          ...restData // Incluir todos los demás datos del servidor (connections, uptime, timestamp)
        }
      }
    } catch (error: any) {
      // Log del error para debugging
      console.error('[API] Error en health check del WebSocket:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: wsUrl
      })
      
      // Si hay un error (servidor offline, timeout, etc.), retornar estado offline
      return {
        success: false,
        data: {
          status: 'offline',
          error: error.message || 'Error de conexión',
          connections: 0,
          uptime: 0
        }
      }
    }
  }

  // Generic HTTP methods for admin routes
  get(url: string, config?: any) {
    return this.client.get(url, config)
  }

  post(url: string, data?: any, config?: any) {
    return this.client.post(url, data, config)
  }

  put(url: string, data?: any, config?: any) {
    return this.client.put(url, data, config)
  }

  patch(url: string, data?: any, config?: any) {
    return this.client.patch(url, data, config)
  }

  delete(url: string, config?: any) {
    return this.client.delete(url, config)
  }

  async updateMyStore(storeId: string, storeData: any) {
    const response = await this.client.put(`/stores/my-stores/${storeId}`, storeData)
    return response.data
  }

  async updateStoreLogo(storeId: string, logoUrl: string) {
    const response = await this.client.put(`/stores/my-stores/${storeId}/logo`, { logo_url: logoUrl })
    return response.data
  }

  async updateStoreCover(storeId: string, coverImageUrl: string) {
    const response = await this.client.put(`/stores/my-stores/${storeId}/cover`, { cover_image_url: coverImageUrl })
    return response.data
  }

  async deleteStoreLogo(storeId: string) {
    const response = await this.client.delete(`/stores/my-stores/${storeId}/logo`)
    return response.data
  }

  async deleteStoreCover(storeId: string) {
    const response = await this.client.delete(`/stores/my-stores/${storeId}/cover`)
    return response.data
  }

  async getStoreProducts(storeId: string) {
    const response = await this.client.get(`/products/store/${storeId}/products`)
    return response.data
  }

  async createProduct(storeId: string, productData: any) {
    const response = await this.client.post(`/products/store/${storeId}/products`, productData)
    return response.data
  }

  async updateProduct(productId: string, productData: any) {
    const response = await this.client.put(`/products/${productId}`, productData)
    return response.data
  }

  async deleteProduct(productId: string) {
    const response = await this.client.delete(`/products/${productId}`)
    return response.data
  }

  async getProductByBarcode(storeId: string, barcode: string) {
    const response = await this.client.get(`/products/store/${storeId}/barcode/${barcode}`)
    return response.data
  }

  async updateProductStock(productId: string, quantity: number, operation: 'add' | 'subtract' | 'set') {
    const response = await this.client.patch(`/products/${productId}/stock`, {
      quantity,
      operation
    })
    return response.data
  }

  async updateProductStockByBarcode(storeId: string, barcode: string, quantity: number, operation: 'add' | 'subtract' | 'set') {
    const response = await this.client.patch(`/products/store/${storeId}/barcode/${barcode}/stock`, {
      quantity,
      operation
    })
    return response.data
  }

  async getStoreCategories(storeId: string) {
    const response = await this.client.get(`/products/store/${storeId}/categories`)
    return response.data
  }

  async createCategory(storeId: string, categoryData: any) {
    const response = await this.client.post(`/products/store/${storeId}/categories`, categoryData)
    return response.data
  }

  async updateCategory(categoryId: string, categoryData: any) {
    const response = await this.client.put(`/products/categories/${categoryId}`, categoryData)
    return response.data
  }

  async getStoreOrders(storeId: string, params?: any) {
    const response = await this.client.get(`/orders/store/${storeId}`, { params })
    return response.data
  }

  async updateOrderStatus(orderId: string, statusData: any) {
    const response = await this.client.patch(`/orders/${orderId}/status`, statusData)
    return response.data
  }

  // Delivery-specific methods
  async pickupOrder(orderId: string) {
    const response = await this.client.patch(`/orders/${orderId}/pickup`)
    return response.data
  }

  async startDelivery(orderId: string) {
    const response = await this.client.patch(`/orders/${orderId}/start-delivery`)
    return response.data
  }

  async completeDelivery(orderId: string, deliveryData: {
    paymentMethod: 'cash' | 'card' | 'digital_wallet' | 'debt'
    items: Array<{
      itemId: string
      isPaid: boolean
      quantityDelivered?: number
    }>
    paymentBreakdown?: Array<{
      denominationId: string
      quantity: number
    }>
  }) {
    const response = await this.client.patch(`/orders/${orderId}/complete-delivery`, deliveryData)
    return response.data
  }

  async getStoreEmployees(storeId: string) {
    const response = await this.client.get(`/auth/store/${storeId}/employees`)
    return response.data
  }

  async hireEmployee(storeId: string, employeeData: any) {
    const response = await this.client.post(`/auth/store/${storeId}/employees`, employeeData)
    return response.data
  }

  async updateEmployeeStatus(employeeId: string, statusData: any) {
    const response = await this.client.patch(`/auth/my-store/employees/${employeeId}`, statusData)
    return response.data
  }

  // Profile methods
  async getProfile() {
    const response = await this.client.get('/users/profile')
    return response.data
  }

  async updateProfile(profileData: any) {
    const response = await this.client.put('/users/profile', profileData)
    return response.data
  }

  async updateProfilePhoto(photoUrl: string) {
    const response = await this.client.put('/users/profile/photo', { profile_photo_url: photoUrl })
    return response.data
  }

  async deleteProfilePhoto() {
    const response = await this.client.delete('/users/profile/photo')
    return response.data
  }

  async getProfilePhotoHistory() {
    const response = await this.client.get('/users/profile/photo/history')
    return response.data
  }

  // Privacy rights methods
  async exportUserData() {
    const response = await this.client.get('/users/export-data', {
      responseType: 'blob'
    })
    // Create download link for PDF
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `colmafacil-datos-personales-${Date.now()}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    return { success: true, message: 'Datos exportados exitosamente' }
  }

  async deleteAccount(confirmation: string) {
    const response = await this.client.delete('/users/account', {
      data: { confirmation }
    })
    return response.data
  }

  async restoreProfilePhoto(historyId: string) {
    const response = await this.client.post(`/users/profile/photo/restore/${historyId}`)
    return response.data
  }

  async updateDeliveryAvailability(available: boolean) {
    const response = await this.client.patch('/users/profile/delivery-availability', { available })
    return response.data
  }

  async getOrders(params?: any) {
    const response = await this.client.get('/orders/my-orders', { params })
    return response.data
  }

  async verifyToken() {
    const response = await this.client.get('/auth/verify')
    return response.data
  }

  // Store methods
  async getStores(params?: any) {
    const response = await this.client.get('/stores', { params })
    return response.data
  }

  async getPublicStats() {
    const response = await this.client.get('/stores/stats')
    return response.data
  }

  async getStore(storeId: string) {
    const response = await this.client.get(`/stores/${storeId}`)
    return response.data
  }


  async updateStore(storeId: string, storeData: any) {
    const response = await this.client.put(`/stores/${storeId}`, storeData)
    return response.data
  }

  // Product methods
  async getProducts(params?: any) {
    const response = await this.client.get('/products', { params })
    return response.data
  }

  async getProduct(productId: string) {
    const response = await this.client.get(`/products/${productId}`)
    return response.data
  }

  async searchProducts(query: string, limit: number = 10) {
    const response = await this.client.get('/products/search', {
      params: { q: query, limit }
    })
    return response.data
  }

  // Order methods
  async getOrder(orderId: string) {
    const response = await this.client.get(`/orders/${orderId}`)
    return response.data
  }

  async createOrder(orderData: any) {
    const response = await this.client.post('/orders', orderData)
    return response.data
  }

  async cancelOrder(orderId: string) {
    const response = await this.client.patch(`/orders/${orderId}/cancel`)
    return response.data
  }


  async getAddresses() {
    const response = await this.client.get('/users/addresses')
    return response.data
  }

  async createAddress(addressData: any) {
    const response = await this.client.post('/users/addresses', addressData)
    return response.data
  }

  async updateAddress(addressId: string, addressData: any) {
    const response = await this.client.put(`/users/addresses/${addressId}`, addressData)
    return response.data
  }

  async deleteAddress(addressId: string) {
    const response = await this.client.delete(`/users/addresses/${addressId}`)
    return response.data
  }

  // Admin methods
  async getAdminStats() {
    const response = await this.client.get('/admin/stats')
    return response.data
  }

  async getPendingStores() {
    const response = await this.client.get('/admin/stores/pending')
    return response.data
  }

  async approveStore(storeId: string) {
    const response = await this.client.post(`/admin/stores/${storeId}/approve`)
    return response.data
  }

  async rejectStore(storeId: string, reason?: string) {
    const response = await this.client.post(`/admin/stores/${storeId}/reject`, { reason })
    return response.data
  }

  async suspendStore(storeId: string, reason?: string) {
    const response = await this.client.patch(`/admin/stores/${storeId}/status`, {
      isActive: false,
      reason
    })
    return response.data
  }

  async activateStore(storeId: string) {
    const response = await this.client.patch(`/admin/stores/${storeId}/status`, {
      isActive: true
    })
    return response.data
  }

  async getAdminStore(storeId: string) {
    const response = await this.client.get(`/admin/stores/${storeId}`)
    return response.data
  }

  async updateAdminStore(storeId: string, storeData: any) {
    const response = await this.client.put(`/admin/stores/${storeId}`, storeData)
    return response.data
  }

  // Admin Orders methods
  async getAdminOrders(params?: any) {
    const response = await this.client.get('/admin/orders', { params })
    return response.data
  }

  async getAdminOrderStats() {
    const response = await this.client.get('/admin/orders/stats')
    return response.data
  }

  async getAdminOrder(orderId: string) {
    const response = await this.client.get(`/admin/orders/${orderId}`)
    return response.data
  }

  // Platform Settings API methods
  async getPlatformSettings() {
    const response = await this.client.get('/admin/platform-settings')
    return response.data
  }

  async getPlatformSetting(key: string) {
    const response = await this.client.get(`/admin/platform-settings/${key}`)
    return response.data
  }

  async updatePlatformSetting(key: string, data: any) {
    const response = await this.client.put(`/admin/platform-settings/${key}`, data)
    return response.data
  }

  async deletePlatformSetting(key: string) {
    const response = await this.client.delete(`/admin/platform-settings/${key}`)
    return response.data
  }

  // Public platform settings (no auth required)
  async getPublicPlatformSettings() {
    const response = await this.client.get('/platform/settings/public')
    return response.data
  }

  // Store development mode
  async toggleStoreDevelopmentMode(storeId: string, developmentMode: boolean) {
    const response = await this.client.patch(`/admin/stores/${storeId}/development-mode`, {
      development_mode: developmentMode
    })
    return response.data
  }

  // User development access
  async toggleUserDevelopmentAccess(userId: string, developmentAccess: boolean) {
    const response = await this.client.patch(`/admin/users/${userId}/development-access`, {
      development_access: developmentAccess
    })
    return response.data
  }

  async getUsersWithDevelopmentAccess() {
    const response = await this.client.get('/admin/users/development-access')
    return response.data
  }

  // Upload/Image methods
  async listImages(folder: string = 'products', maxKeys: number = 100, prefix?: string) {
    const params = new URLSearchParams({ folder, maxKeys: maxKeys.toString() })
    if (prefix) params.append('prefix', prefix)
    const response = await this.client.get(`/upload/list?${params.toString()}`)
    return response.data
  }
}

export const api = new ApiClient()
// Category API methods
export const categoryApi = {
  // Get all global categories with hierarchy
  getGlobalCategories: async (language = 'es') => {
    const response = await api.get(`/categories/global?language=${language}`)
    return response.data.data || []
  },

  // Get global category by ID
  getGlobalCategory: async (id: string, language = 'es') => {
    const response = await api.get(`/categories/global/${id}?language=${language}`)
    return response.data.data
  },

  // Create global category
  createGlobalCategory: async (categoryData: {
    name: string
    slug: string
    description?: string
    image_url?: string
    parent_id?: string
    sort_order?: number
  }) => {
    const response = await api.post('/categories/global', categoryData)
    return response.data.data
  },

  // Update global category
  updateGlobalCategory: async (id: string, categoryData: Partial<{
    name: string
    slug: string
    description: string
    image_url: string
    parent_id: string
    sort_order: number
  }>) => {
    const response = await api.put(`/categories/global/${id}`, categoryData)
    return response.data.data
  },

  // Delete global category
  deleteGlobalCategory: async (id: string) => {
    const response = await api.delete(`/categories/global/${id}`)
    return response
  },

  // Get translations for a category
  getCategoryTranslations: async (id: string) => {
    const response = await api.get(`/categories/global/${id}/translations`)
    return response.data.data || []
  },

  // Save translation for a category
  saveCategoryTranslation: async (id: string, language: string, translation: {
    name: string
    description?: string
  }) => {
    const response = await api.put(`/categories/global/${id}/translations/${language}`, translation)
    return response.data.data
  },

  // Delete translation for a category
  deleteCategoryTranslation: async (id: string, language: string) => {
    const response = await api.delete(`/categories/global/${id}/translations/${language}`)
    return response
  },

  // Get store categories (for assignment)
  getStoreCategories: async (storeId: string, language = 'es') => {
    const response = await api.get(`/categories/store/${storeId}?language=${language}`)
    return response.data.data || []
  }
}

// News API methods
export const newsApi = {
  getNews: async (limit = 10) => {
    const response = await api.get(`/news/public?limit=${limit}`)
    return response.data
  },

  getAllNews: async (limit = 20, offset = 0) => {
    const response = await api.get(`/news/all?limit=${limit}&offset=${offset}`)
    return response.data
  },

  getNewsById: async (id: string) => {
    const response = await api.get(`/news/${id}`)
    return response.data
  },

  createNews: async (newsData: FormData) => {
    const response = await api.post('/news', newsData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  updateNews: async (id: string, newsData: FormData) => {
    const response = await api.put(`/news/${id}`, newsData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  deleteNews: async (id: string) => {
    const response = await api.delete(`/news/${id}`)
    return response.data
  },

  getNewsImageHistory: async (id: string) => {
    const response = await api.get(`/news/${id}/image-history`)
    return response.data
  },

  getCarouselVisibility: async () => {
    const response = await api.get('/news/carousel/visibility')
    return response.data
  },

  updateCarouselVisibility: async (role: string, isVisible: boolean) => {
    const response = await api.put('/news/carousel/visibility', { role, is_visible: isVisible })
    return response.data
  },

  getNewsRoleVisibility: async (id: string) => {
    const response = await api.get(`/news/${id}/role-visibility`)
    return response.data
  },

  updateNewsRoleVisibility: async (id: string, roles: Array<{ role: string; is_visible: boolean }>) => {
    const response = await api.put(`/news/${id}/role-visibility`, { roles })
    return response.data
  },

  // Upload endpoints
  listImages: async (folder: string = 'products', maxKeys: number = 100, prefix?: string) => {
    const params = new URLSearchParams({ folder, maxKeys: maxKeys.toString() })
    if (prefix) params.append('prefix', prefix)
    const response = await api.get(`/upload/list?${params.toString()}`)
    return response.data
  }
}

// Delivery Wallet API methods
export const deliveryWalletApi = {
  // Get delivery wallet
  getWallet: async () => {
    const response = await api.get('/delivery/wallet')
    return response.data
  },

  // Get pending transfers
  getPendingTransfers: async () => {
    const response = await api.get('/delivery/wallet/pending-transfers')
    return response.data
  },

  // Confirm transfer
  confirmTransfer: async (transferId: string) => {
    const response = await api.post(`/delivery/wallet/confirm-transfer/${transferId}`)
    return response.data
  },

  // Receive money from store (deprecated - use confirmTransfer)
  receiveMoney: async (data: {
    amount: number
    breakdown?: Array<{ denominationId: string; quantity: number }>
  }) => {
    const response = await api.post('/delivery/wallet/receive-money', data)
    return response.data
  },

  // Deliver money to store
  deliverMoney: async (data: {
    amount: number
    breakdown?: Array<{ denominationId: string; quantity: number }>
    receivedBy?: string
  }) => {
    const response = await api.post('/delivery/wallet/deliver-money', data)
    return response.data
  }
}

// Store Money Transfer API methods (for admin/dispatcher)
export const storeTransferApi = {
  // Create transfer to delivery
  createTransfer: async (storeId: string, data: {
    deliveryId: string
    amount: number
    breakdown: Array<{ denominationId: string; quantity: number }>
    description?: string
  }) => {
    const response = await api.post(`/delivery/store/${storeId}/transfer-money`, data)
    return response.data
  },

  // Get transfers for a store
  getTransfers: async (storeId: string, status?: string) => {
    const params = status ? { status } : {}
    const response = await api.get(`/delivery/store/${storeId}/transfers`, { params })
    return response.data
  },

  // Cancel transfer
  cancelTransfer: async (storeId: string, transferId: string) => {
    const response = await api.post(`/delivery/store/${storeId}/transfers/${transferId}/cancel`)
    return response.data
  },

  // Get all delivery wallets for a store
  getStoreWallets: async (storeId: string) => {
    const response = await api.get(`/delivery/store/${storeId}/wallets`)
    return response.data
  }
}

// Currency Denominations API methods
export const currencyDenominationsApi = {
  // Get all denominations (public - for deliveries and others)
  getDenominations: async () => {
    const response = await api.get('/delivery/currency-denominations')
    return response.data
  },

  // Get all denominations (admin only - includes inactive)
  getAllDenominations: async () => {
    const response = await api.get('/admin/currency-denominations')
    return response.data
  },

  // Create denomination
  createDenomination: async (data: {
    denomination: number
    name: string
    type: 'bill' | 'coin'
    sort_order?: number
  }) => {
    const response = await api.post('/admin/currency-denominations', data)
    return response.data
  },

  // Update denomination
  updateDenomination: async (id: string, data: {
    name?: string
    type?: 'bill' | 'coin'
    is_active?: boolean
    sort_order?: number
  }) => {
    const response = await api.put(`/admin/currency-denominations/${id}`, data)
    return response.data
  },

  // Delete denomination
  deleteDenomination: async (id: string) => {
    const response = await api.delete(`/admin/currency-denominations/${id}`)
    return response.data
  }
}

export default api
